import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Popover } from '@heroui/react';
import { UserCard, type UserCardUser } from '@/components/userCard/UserCard';

/**
 * Wraps any element (an avatar, a username) into a click target that opens
 * the shared {@link UserCard} popover. Used from the member list and from
 * message authors so a click on the circle OR the nick brings up the card.
 */
export function UserCardTrigger(props: {
  user: UserCardUser;
  guildId?: string | null;
  className?: string;
  /** aria-label for the trigger (e.g. "Профиль Иван"). */
  label?: string;
  /** Pass when the trigger lives in a voice room — enables per-listener
   *  volume + local mute controls in the card. */
  voice?: { channelId: string | null };
  /** Own profile only — forwarded to UserCard for the status picker + a
   *  "go to settings" row. */
  onOpenSettings?: () => void;
  /** Hide the "Написать сообщение" action — set when the trigger is the header
   *  of that user's own 1:1 DM (we're already there). */
  hideMessage?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);

  function close() {
    setOpen(false);
  }

  return (
    <>
      <Popover isOpen={open} onOpenChange={setOpen}>
        <Popover.Trigger aria-label={props.label} className={props.className}>
          {props.children}
        </Popover.Trigger>
        {/* max-h + scroll so a tall card (roles + voice controls) never spills
            past a short viewport — it flips/scrolls instead of clipping. */}
        <Popover.Content
          className="app-profile-popover w-80 max-w-[calc(100vw-1.5rem)] overflow-y-auto overflow-x-hidden"
          placement="bottom start"
        >
          <Popover.Dialog className="p-0">
            <UserCard
              user={props.user}
              guildId={props.guildId}
              voice={props.voice}
              onOpenSettings={props.onOpenSettings}
              hideMessage={props.hideMessage}
              onClose={close}
              onOpenFull={() => {
                close();
                setFullOpen(true);
              }}
            />
          </Popover.Dialog>
        </Popover.Content>
      </Popover>

      {/* Full profile — the same card, larger and centred. Lives here (not
          inside the popover) so clicks on the modal don't trip the popover's
          outside-click close. */}
      <Modal.Backdrop className="app-viewport-overlay" isOpen={fullOpen} variant="blur" onOpenChange={setFullOpen}>
        <Modal.Container size="lg" placement="center" className="app-profile-container">
          <Modal.Dialog aria-label={t('prof.card.profile')} className="app-profile-dialog w-[760px] max-w-[94vw] overflow-y-auto overflow-x-hidden p-0">
            <UserCard
              user={props.user}
              guildId={props.guildId}
              voice={props.voice}
              variant="dialog"
              onOpenSettings={props.onOpenSettings}
              hideMessage={props.hideMessage}
              onClose={() => setFullOpen(false)}
            />
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </>
  );
}
